<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useScroll } from "@vueuse/core";
import { vOnClickOutside } from "@vueuse/components";
import { scrollLock } from "@magnit/core/src/helpers/scrollLock";
import VSearch from "@magnit/core/src/containers/VSearch/VSearch.vue";
import { useAnalyticManager } from "@magnit/analytic-events";

const props = defineProps<{
  modelValue: string;
  openSuggest: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "update:openSuggest": [value: boolean];
  "click:cancel": [];
  submit: [value: string];
  clear: [];
  focus: [];
  blur: [];
}>();

const searchFocused = ref(false);
const searchInputRef = ref<InstanceType<typeof VSearch> | null>(null);
const suggestsWrapperRef = ref<HTMLDivElement | null>(null);
const { y: suggestsScrollY } = useScroll(suggestsWrapperRef);
const { send } = useAnalyticManager();

const onSubmit = () => {
  if (!props.modelValue || props.modelValue.length < 3) {
    searchInputRef.value?.focus();
    searchFocused.value = true;
    return;
  }
  closeSuggests();
  send("Search:Button:Find:Click");
  emit("submit", props.modelValue);
};

const onSearchKeydownEnter = (e: KeyboardEvent) => {
  if (!props.modelValue || props.modelValue.length < 3) {
    e.preventDefault();
  }
};

const onSearchClick = () => {
  send("Toolbar:Search:Click", {
    query: props.modelValue,
  });
};

const closeSuggests = () => {
  // Т.к. наш метод вызывается по v-on-click-outside,
  // которому вообще фиолетово был ли элемент в фокусе или нет...
  // То здесь отталкиваемся проверкой от внутренного состояния,
  // чтобы избежать лишних "мусорных" эмитов и переключений.
  // Вообще было-бы круто сказать click outside "срабатывай только если элемент был перед этим в фокусе",
  // или чёто типа того...
  // Но я не нашёл такой фичи у него, на момент написания этих строк. Разве что можно игнорит клики по индивидуальным прочим элементам, но это не то.
  if (props.openSuggest) {
    emit("update:openSuggest", false);
    scrollLock.disable();
  }
};

const onSearchCancelClick = () => {
  emit("click:cancel");
  closeSuggests();
  send("Search:Field:Cancel:Click");
};

const onSearchClearClick = () => {
  emit("clear");
  emit("update:modelValue", "");
  send("Search:Field:Clear:Click");
};

watch(searchFocused, (next) => {
  if (next) {
    scrollLock.enable();
    emit("focus");
    emit("update:openSuggest", true);
  } else {
    emit("blur");
  }
});

watch(
  () => props.openSuggest,
  (next) => {
    if (!next) {
      closeSuggests();
    }
  },
);

onMounted(() => {
  send("Toolbar:Search:View");
});
</script>

<template>
  <div v-on-click-outside="closeSuggests" class="header-search">
    <VSearch
      ref="searchInputRef"
      :model-value="modelValue"
      class="header-search__search"
      placeholder="Искать товары"
      :submit-visible="{ xs: false, l: true }"
      input-test-id="search-row"
      button-test-id="search-button"
      @click="onSearchClick"
      @focus="searchFocused = true"
      @blur="searchFocused = false"
      @submit="onSubmit"
      @cancel="onSearchCancelClick"
      @clear="onSearchClearClick"
      @keydown.enter="onSearchKeydownEnter"
      @update:model-value="(e) => emit('update:modelValue', e)"
    />
    <Transition name="header-search-suggest">
      <div
        v-show="openSuggest"
        class="header-search__suggest"
        :class="{
          'header-search__suggest-scrolling': suggestsScrollY > 0,
        }"
        data-test-id="suggest"
      >
        <div ref="suggestsWrapperRef" class="header-search__suggest-content">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="postcss">
.header-search {
  --header-search__sugest-bottom-gap: 50px;

  flex-grow: 1;

  &__search {
    z-index: var(--pl-z-index-level2);
    position: relative;
  }

  &__suggest {
    position: absolute;
    left: 0;
    top: 100%;
    width: 100vw;

    @supports (height: 100dvh) {
      height: calc(100dvh - var(--header-project-nav-height));
    }

    @supports not (height: 100dvh) {
      height: calc(89vh - var(--header-project-nav-height));
    }

    margin-left: calc(var(--pl-unit-x3) * -1);
    background: var(--pl-global-background-primary);
    border-top: 1px solid var(--pl-divider-primary);

    &:empty {
      padding: 0;
      border-top: 0;
    }

    &-content {
      height: auto;
      max-height: 100%;
      overflow: auto;
    }
  }
}

@media (--pl-viewport-from-s) {
  .header-search {
    &__suggest {
      margin-left: calc(var(--pl-unit-x5) * -1);
    }
  }
}

@media (--pl-viewport-from-m) {
  .header-search {
    &__suggest {
      margin-left: calc(var(--pl-unit-x8) * -1);
    }
  }
}

@media (--pl-viewport-from-ml) {
  .header-search {
    &__suggest {
      border-radius: 0 0 var(--pl-unit-x4) var(--pl-unit-x4);
      height: auto;
      max-height: calc(
        100vh - var(--header-project-nav-height) -
          var(--header-search__sugest-bottom-gap)
      );
      overflow: hidden;

      &-content {
        height: auto;
        max-height: calc(
          100vh - var(--header-project-nav-height) -
            var(--header-search__sugest-bottom-gap)
        );
        overflow: auto;
      }

      .pl-header.scrolled-bottom & {
        height: auto;
      }
    }
  }
}

@media (--pl-viewport-from-l) {
  .header-search {
    position: relative;

    &__suggest {
      width: 100%;
      border-top-color: var(--pl-global-background-primary);
      top: calc(100% + var(--pl-unit-x2));
      margin-left: 0;

      &-scrolling {
        border-top-color: var(--pl-divider-primary);
      }
    }
  }
}

.header-search-suggest-enter-active,
.header-search-suggest-leave-active {
  transition: all 0.2s ease-in-out;
}

.header-search-suggest-enter-from,
.header-search-suggest-leave-to {
  opacity: 0.25;
  transform: translateY(-20px);
}
</style>
